import React, {
  useMemo, useEffect, useState, useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  Center, Spinner, Heading, Text, HStack, Box,
} from '@gluestack-ui/themed';

import ScrollableScreenContainer from '../../common/components/ScrollableScreenContainer';

import LegacyGradeReport from './components/LegacyGradeReport';
import GradeReport from './components/GradeReport';
import { ExamAttemptGradeActions } from './components/ExamAttemptNote';
import ResourceSidebar from '../task/ResourceSidebar';

import { useModuleData } from '../../common/hooks/student/useModuleData';
import { gradingConfig } from '../../common/config/grading';
import { useCourseData } from '../../common/hooks/student/useCourseData';
import { useAuthStore } from '../../common/auth/store';
import { useGetExamAttemptReport } from '../../common/api/endpoints/submissions/submissions';

/**
 * GradeScreen displays the evaluation report for a graded module.
 *
 * This screen is INDEPENDENT of global selectedModule state.
 * It relies solely on route params (moduleId, courseId) to fetch and display data.
 * This ensures viewing grades doesn't interfere with active work context.
 */
const GradeScreen = ({ route, navigation }) => {
  const { moduleId, courseId } = route?.params || {};
  const { t } = useTranslation();
  const userId = useAuthStore((state) => state.user?.id);

  // Fetch module data directly using route params (no dependency on selectedModule)
  const { modules, isLoading: isModuleLoading } = useCourseData();

  const module = !!modules && Array.isArray(modules)
    ? modules.find((m) => m.id === moduleId && m.courses?.[0]?.id === courseId)
    : null;

  // Fetch tasks with resources
  const {
    tasks,
    isLoading: isTasksLoading,
    isError: isTasksError,
  } = useModuleData({
    moduleId,
    courseId,
    includes: ['submissions', 'resources'],
  });

  // Memoize the flattened resources array
  const allResources = useMemo(() => {
    if (!isTasksLoading && !isTasksError && tasks) {
      return tasks.flatMap((task) => task?.resources || []);
    }
    return [];
  }, [tasks, isTasksLoading, isTasksError]);

  // Determine which version of the grade report to show
  const shouldUseModernReport = useMemo(() => {
    if (!module?.grading?.gradedAt) return true;
    const gradedDate = new Date(module.grading.gradedAt);
    const cutoffDate = gradingConfig.modernGradingCutoffDate;
    return gradedDate >= cutoffDate;
  }, [module?.grading?.gradedAt]);

  const gradedAttempts = useMemo(
    () => module?.examAttempt?.gradedAttempts ?? [],
    [module?.examAttempt?.gradedAttempts],
  );
  /** null = default to latest graded attempt (avoids archive fetch on first paint). */
  const [selectedAttemptIndex, setSelectedAttemptIndex] = useState(null);

  useEffect(() => {
    setSelectedAttemptIndex(null);
  }, [module?.id]);

  const latestAttemptIndex = gradedAttempts.length > 0 ? gradedAttempts.length - 1 : 0;
  const activeAttemptIndex = selectedAttemptIndex ?? latestAttemptIndex;
  const viewingEarlier = gradedAttempts.length >= 2 && activeAttemptIndex !== latestAttemptIndex;
  const selectedTab = gradedAttempts[activeAttemptIndex];
  const attemptNumber = selectedTab?.number ?? 0;

  const archiveReportQuery = useGetExamAttemptReport(
    userId || '',
    courseId || '',
    moduleId || '',
    attemptNumber,
    {
      query: {
        enabled: viewingEarlier && !!userId && !!courseId && !!moduleId && attemptNumber > 0,
      },
    },
  );

  const handleRetryArchive = useCallback(async () => {
    await archiveReportQuery.refetch();
  }, [archiveReportQuery]);

  const reportModule = useMemo(() => {
    if (!module) {
      return null;
    }
    const baseGrading = {
      grade: module.grading?.grade || 0,
      comment: module.grading?.comment,
      dimensions: module.grading?.dimensions || {},
      gradedAt: module.grading?.gradedAt,
      ...(module.grading?.adminOverride ? { adminOverride: module.grading.adminOverride } : {}),
    };
    const tab = gradedAttempts[activeAttemptIndex];
    const grading = gradedAttempts.length >= 2 && tab
      ? {
        ...baseGrading,
        grade: tab.grade,
        comment: tab.comment ?? '',
      }
      : baseGrading;

    const archivePayload = archiveReportQuery.data?.data?.data;
    const gradingConfigSnapshot = viewingEarlier && archivePayload?.gradingConfigSnapshot
      ? archivePayload.gradingConfigSnapshot
      : module.gradingConfigSnapshot;

    return {
      ...module,
      title: module.title,
      grading,
      gradingConfigSnapshot,
      imageUrl: module.imageUrl,
    };
  }, [module, gradedAttempts, activeAttemptIndex, viewingEarlier, archiveReportQuery.data?.data]);

  const reportTasks = useMemo(() => {
    const archiveTasks = archiveReportQuery.data?.data?.data?.tasks;
    if (viewingEarlier) {
      if (archiveReportQuery.isSuccess && archiveTasks) {
        return archiveTasks;
      }
      return [];
    }
    return tasks;
  }, [viewingEarlier, archiveReportQuery.isSuccess, archiveReportQuery.data?.data, tasks]);

  const attemptNote = (
    <ExamAttemptGradeActions
      module={module}
      courseId={courseId}
      navigation={navigation}
    />
  );

  useEffect(() => {
    if (!isModuleLoading && !module) {
      navigation.navigate('CourseScreen');
    }
  }, [module, isModuleLoading, navigation]);

  if (isModuleLoading) {
    return (
      <Center>
        <Spinner />
      </Center>
    );
  }

  if (!module) {
    return null;
  }

  const archivePending = viewingEarlier && !archiveReportQuery.isSuccess
    && (archiveReportQuery.isLoading || archiveReportQuery.isFetching);
  const archiveFailed = viewingEarlier && archiveReportQuery.isError;
  const archiveState = archivePending ? 'loading' : archiveFailed ? 'error' : 'ready';

  return (
    <ScrollableScreenContainer>
      <HStack flex={1}>
        <Box $md-px="$4" px="$2" py="$6" flex={1}>
          {module.state.studentWorkflowState !== 'graded' && <Text>Module is not graded yet</Text>}
          {shouldUseModernReport ? (
            <GradeReport
              module={reportModule}
              tasks={reportTasks}
              gradedAttempts={gradedAttempts}
              selectedAttemptIndex={activeAttemptIndex}
              onSelectAttempt={setSelectedAttemptIndex}
              afterHeader={attemptNote}
              archiveState={archiveState}
              onRetryArchive={handleRetryArchive}
            />
          ) : (
            <LegacyGradeReport module={module} tasks={tasks} isLoading={isTasksLoading} />
          )}
        </Box>

        {!isTasksLoading && !isTasksError && allResources.length > 0 && (
          <Box
            $base-display="none"
            $lg-display="flex"
            $lg-w={250}
            $xl-w={400}
            px="$6"
            py="$6"
            borderLeftWidth={1}
            borderLeftColor="$borderLight200"
          >
            <Heading mb="$4">{t('student.task.resourcePanel.title')}</Heading>
            <ResourceSidebar resources={allResources} />
          </Box>
        )}
      </HStack>
    </ScrollableScreenContainer>
  );
};

export default GradeScreen;
